import { Controller } from "@hotwired/stimulus"

// "Show more" pagination for catalog/list pages. Attach to a wrapper that
// contains the "load more" link and a progress indicator. On click:
//   1. Show the progress target.
//   2. Fetch the link's href.
//   3. Replace this whole wrapper with the response HTML (which contains
//      additional items + a new wrapper with its own controller).
//
// On connect, syncs `.search-items-count` elements with `totalValue` so the
// catalog header counter stays in sync as more pages load.
//
// Usage:
//   <div id="js-loadmore_container"
//        data-controller="load-more"
//        data-load-more-total-value="<%= @materials.total_count %>">
//     <%= link_to ..., data: { action: "click->load-more#fetch" } %>
//     <div class="progress hide" data-load-more-target="progress">…</div>
//   </div>
export default class extends Controller {
  static targets = ["progress"]
  static values  = { total: Number }

  connect() {
    if (this.hasTotalValue) {
      document.querySelectorAll(".search-items-count").forEach(el => {
        el.textContent = String(this.totalValue)
      })
    }
  }

  async fetch(event) {
    event.preventDefault()
    const url = event.currentTarget.getAttribute("href")
    if (!url) return

    if (this.hasProgressTarget) this.progressTarget.classList.remove("hide")
    event.currentTarget.style.pointerEvents = "none"

    try {
      // `X-Requested-With` is what jQuery's $.ajax used to set; Rails uses it
      // (`request.xhr?`) to decide whether to render the partial only vs the
      // full layout. Without it, the server returns the entire page and we'd
      // end up nesting the header/footer in the middle of the list.
      const response = await window.fetch(url, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }
      })
      const html = await response.text()
      // The partial renders multiple sibling root elements (the new items +
      // the next wrapper). Use a template + DocumentFragment so all of them
      // get inserted in the original wrapper's place.
      const tmp = document.createElement("template")
      tmp.innerHTML = html
      this.element.replaceWith(tmp.content)
    } catch (e) {
      if (this.hasProgressTarget) this.progressTarget.classList.add("hide")
      event.currentTarget.style.pointerEvents = ""
      throw e
    }
  }
}
